import type { Subscription } from 'rxjs';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import {
  MESSAGE_HISTORY_TAKE,
  TRANSCRIPT_DELTA_TAKE,
  TRANSCRIPT_REFRESH_DELAY_MS,
  TRANSCRIPT_REFRESH_MIN_INTERVAL_MS,
  WEB_CHAT_TRANSCRIPT_REFRESH_RETRY_DELAY_MS,
  type WebChatState,
} from './web-chat.models';
import { WebChatStateStore } from './web-chat-state.store';

export type WebChatTranscriptTimer = { cancel(): void };

export interface WebChatTranscriptPlatform {
  now(): number;
  setTimer(callback: () => void, delayMs: number): WebChatTranscriptTimer;
}

const browserWebChatTranscriptPlatform: WebChatTranscriptPlatform = {
  now: () => Date.now(),
  setTimer: (callback, delayMilliseconds) => {
    const handle = setTimeout(callback, delayMilliseconds);
    return { cancel: () => clearTimeout(handle) };
  },
};

interface TranscriptSessionOwner {
  readonly sessionId: string;
  readonly generation: number;
}

interface TranscriptRequestOwner {
  readonly session: TranscriptSessionOwner;
  subscription: Subscription | null;
}

export class WebChatTranscriptRuntime {
  private activeSession: TranscriptSessionOwner | null = null;
  private generation = 0;
  private pendingTimer: WebChatTranscriptTimer | null = null;
  private pendingTimerSession: TranscriptSessionOwner | null = null;
  private activeRequest: TranscriptRequestOwner | null = null;
  private queuedRefresh = false;
  private queuedDelayMilliseconds = TRANSCRIPT_REFRESH_DELAY_MS;
  private pendingDeltaOnly = false;
  private retryBudget = 0;
  private expectedMinimumCount: number | null = null;
  private lastRefreshTimestamp: number | null = null;
  private destroyed = false;

  constructor(
    private readonly state: WebChatStateStore,
    private readonly messages: WebChatMessageRuntime,
    private readonly platform: WebChatTranscriptPlatform = browserWebChatTranscriptPlatform,
  ) {}

  scheduleTranscriptRefresh(
    delayMilliseconds = TRANSCRIPT_REFRESH_DELAY_MS,
    retryBudget = 0,
    deltaOnly = false,
  ): void {
    const owner = this.acquireCurrentSession();
    if (!owner) {
      return;
    }

    this.queueRefresh(delayMilliseconds, retryBudget, deltaOnly);
    if (!this.activeRequest && !this.pendingTimer) {
      this.scheduleQueuedRefresh(owner);
    }
  }

  refreshTranscriptForActiveSession(): void {
    const owner = this.acquireCurrentSession();
    if (!owner) {
      return;
    }

    if (this.activeRequest) {
      this.queueRefresh(TRANSCRIPT_REFRESH_DELAY_MS, 0, false);
      return;
    }

    this.cancelPendingTimer();
    if (!this.queuedRefresh) {
      this.queueRefresh(0, 0, false);
    }
    this.startRefresh(owner);
  }

  reset(): void {
    this.invalidateSession();
  }

  destroy(): void {
    this.destroyed = true;
    this.invalidateSession();
  }

  private acquireCurrentSession(): TranscriptSessionOwner | null {
    if (this.destroyed) {
      return null;
    }

    const sessionId = readExactSessionId(
      this.state.stateSubject.value.session?.sessionId,
    );
    if (!sessionId) {
      this.invalidateSession();
      return null;
    }

    if (this.activeSession?.sessionId === sessionId) {
      return this.activeSession;
    }

    this.invalidateSession();
    const owner = { sessionId, generation: this.generation };
    this.activeSession = owner;
    return owner;
  }

  private queueRefresh(
    delayMilliseconds: number,
    retryBudget: number,
    deltaOnly: boolean,
  ): void {
    const boundedDelay = Math.max(0, delayMilliseconds);
    this.queuedDelayMilliseconds = this.queuedRefresh
      ? Math.min(this.queuedDelayMilliseconds, boundedDelay)
      : boundedDelay;
    this.queuedRefresh = true;
    this.pendingDeltaOnly ||= deltaOnly;
    this.retryBudget = Math.max(this.retryBudget, retryBudget);

    if (retryBudget > 0) {
      this.expectedMinimumCount = Math.max(
        this.expectedMinimumCount ?? 0,
        this.state.stateSubject.value.messages.length + 1,
      );
    }
  }

  private scheduleQueuedRefresh(owner: TranscriptSessionOwner): void {
    if (!this.owns(owner) || !this.queuedRefresh || this.pendingTimer) {
      return;
    }

    const elapsed =
      this.lastRefreshTimestamp == null
        ? Number.POSITIVE_INFINITY
        : this.platform.now() - this.lastRefreshTimestamp;
    const effectiveDelay = Math.max(
      this.queuedDelayMilliseconds,
      TRANSCRIPT_REFRESH_MIN_INTERVAL_MS - elapsed,
    );
    this.pendingTimerSession = owner;
    const handle = this.platform.setTimer(() => {
      if (this.pendingTimerSession !== owner) {
        return;
      }

      this.pendingTimer = null;
      this.pendingTimerSession = null;
      if (this.owns(owner)) {
        this.startRefresh(owner);
      }
    }, effectiveDelay);
    if (this.pendingTimerSession === owner) {
      this.pendingTimer = handle;
    } else {
      handle.cancel();
    }
  }

  private startRefresh(owner: TranscriptSessionOwner): void {
    if (!this.owns(owner) || this.activeRequest) {
      return;
    }

    const isDelta = this.pendingDeltaOnly;
    this.queuedRefresh = false;
    this.queuedDelayMilliseconds = TRANSCRIPT_REFRESH_DELAY_MS;
    this.pendingDeltaOnly = false;
    this.lastRefreshTimestamp = this.platform.now();

    const request: TranscriptRequestOwner = {
      session: owner,
      subscription: null,
    };
    this.activeRequest = request;

    try {
      const subscription = this.state.chatClient
        .listSessionMessages({
          sessionId: owner.sessionId,
          query: {
            take: isDelta ? TRANSCRIPT_DELTA_TAKE : MESSAGE_HISTORY_TAKE,
            search:
              this.state.stateSubject.value.messageSearchTerm || undefined,
          },
        })
        .subscribe({
          next: (incoming) => this.applyMessages(request, incoming),
          error: () => this.finishRequest(request, true),
          complete: () => this.finishRequest(request, false),
        });
      request.subscription = subscription;
    } catch {
      this.finishRequest(request, true);
    }
  }

  private applyMessages(
    request: TranscriptRequestOwner,
    incoming: Parameters<WebChatMessageRuntime['mergeMessages']>[1],
  ): void {
    if (!this.ownsRequest(request)) {
      return;
    }

    const current = this.state.stateSubject.value;
    const priorCount = current.messages.length;
    const serverMessages = this.messages.sortMessagesChronologically(
      [...incoming].reverse(),
    );
    const nextMessages = this.messages.mergeMessages(
      current.messages,
      serverMessages,
    );
    const nextSessionFiles = this.messages.mergeSessionFiles(
      current.sessionFiles,
      this.messages.collectSessionFilesFromMessages(nextMessages),
    );
    const responsePatch: Partial<WebChatState> = {};
    const hasFreshInboundResponse =
      this.messages.hasNewInboundResponse(nextMessages);

    if (current.finalRenderMessage && hasFreshInboundResponse) {
      responsePatch.finalRenderMessage = null;
    }
    if (current.isAwaitingResponse && hasFreshInboundResponse) {
      responsePatch.isAwaitingResponse = false;
      responsePatch.activeThoughtText = null;
      responsePatch.finalRenderMessage = null;
      responsePatch.isThoughtChainActive = false;
    }

    if (!this.ownsRequest(request)) {
      return;
    }
    if (current.isAwaitingResponse && hasFreshInboundResponse) {
      this.state.clearAwaitingResponseTimeout();
    }
    this.state.patch({
      messages: nextMessages,
      sessionFiles: nextSessionFiles,
      ...responsePatch,
    });

    if (
      this.expectedMinimumCount == null ||
      nextMessages.length >= this.expectedMinimumCount
    ) {
      this.retryBudget = 0;
      this.expectedMinimumCount = null;
      return;
    }

    if (this.retryBudget > 0 && nextMessages.length <= priorCount) {
      this.retryBudget -= 1;
      this.queueRefresh(
        WEB_CHAT_TRANSCRIPT_REFRESH_RETRY_DELAY_MS,
        this.retryBudget,
        true,
      );
      return;
    }

    this.retryBudget = 0;
    this.expectedMinimumCount = null;
  }

  private finishRequest(
    request: TranscriptRequestOwner,
    failed: boolean,
  ): void {
    if (!this.ownsRequest(request)) {
      return;
    }

    this.activeRequest = null;
    if (failed && this.retryBudget > 0) {
      this.retryBudget -= 1;
      this.queueRefresh(
        WEB_CHAT_TRANSCRIPT_REFRESH_RETRY_DELAY_MS,
        this.retryBudget,
        true,
      );
    } else if (failed && !this.queuedRefresh) {
      this.retryBudget = 0;
      this.expectedMinimumCount = null;
    }

    if (this.queuedRefresh) {
      this.scheduleQueuedRefresh(request.session);
    }
  }

  private owns(owner: TranscriptSessionOwner): boolean {
    return (
      !this.destroyed &&
      this.activeSession === owner &&
      owner.generation === this.generation &&
      readExactSessionId(this.state.stateSubject.value.session?.sessionId) ===
        owner.sessionId
    );
  }

  private ownsRequest(request: TranscriptRequestOwner): boolean {
    return this.activeRequest === request && this.owns(request.session);
  }

  private cancelPendingTimer(): void {
    const timer = this.pendingTimer;
    this.pendingTimer = null;
    this.pendingTimerSession = null;
    timer?.cancel();
  }

  private invalidateSession(): void {
    this.generation += 1;
    this.activeSession = null;
    this.cancelPendingTimer();

    const request = this.activeRequest;
    this.activeRequest = null;
    if (request) {
      request.subscription?.unsubscribe();
    }

    this.queuedRefresh = false;
    this.queuedDelayMilliseconds = TRANSCRIPT_REFRESH_DELAY_MS;
    this.pendingDeltaOnly = false;
    this.retryBudget = 0;
    this.expectedMinimumCount = null;
    this.lastRefreshTimestamp = null;
  }
}

function readExactSessionId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && value === value.trim()
    ? value
    : null;
}
